import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import { computeQualityScore, computeFinalScore } from '../lib/listingQuality';
import { rotateForSession } from '../lib/boostRotation';
import { hasCarousel } from '../lib/boostHelpers';
import PropertyCard from './PropertyCard';
import './FeaturedListings.css';

// Set to `false` to revert the carousel to the legacy image-fill cards.
const USE_WHITE_PROPERTY_CARDS = true;

function mapRow(row) {
  const listing = {
    id:               row.id,
    title:            row.title,
    price:            row.price,
    priceType:        row.price_label,
    type:             row.type,
    bedrooms:         row.beds,
    bathrooms:        row.baths,
    size:             row.sqm,
    city:             row.city,
    region:           row.area,
    address:          row.address,
    plotSize:         row.plot_size,
    description:      row.description,
    features:         row.features || [],
    is_boosted:       row.is_boosted || false,
    boost_expires_at: row.boost_expires_at || null,
        carousel_until:  row.carousel_until  || null,
        priority_until:  row.priority_until  || null,
    status:           row.status || 'active',
    sold_at:          row.sold_at || null,
    image:            row.images?.[0] || null,
    images:           row.images || [],
    isFeatured:       row.is_featured,
    contact_phone:    row.contact_phone,
    contact_email:    row.contact_email,
    created_at:       row.created_at || null,
    gradient:         '#F2F1ED',
    agent:            { name: 'CosmoEstates Agent', phone: '+357 25 000 000', agency: 'CosmoEstates' },
  };
  listing.qualityScore = computeQualityScore(listing);
  return listing;
}

function formatPrice(listing) {
  if (!listing.price) return '';
  const isRent = listing.priceType === 'per month' || listing.priceType === 'rent';
  if (isRent) return '€' + listing.price.toLocaleString('en-GB') + '/mo';
  if (listing.price >= 1000000) return '€' + (listing.price / 1000000).toFixed(1) + 'M';
  return '€' + listing.price.toLocaleString('en-GB');
}

export default function FeaturedListings() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [index, setIndex]             = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile]       = useState(false);
  const [cardWidth, setCardWidth]     = useState(340);
  const [vpWidth, setVpWidth]         = useState(1020);
  const touchStartX     = useRef(null);
  const mobileScrollRef = useRef(null);
  const viewportRef     = useRef(null);

  const HEADER = (
    <div className="featuredCarousel__header">
      <span className="featuredCarousel__eyebrow">
        <span className="featuredCarousel__eyebrowLine" />
        <span className="featuredCarousel__eyebrowText">{t('home.boostedListings.title')}</span>
        <span className="featuredCarousel__eyebrowLine" />
      </span>

      <button
        type="button"
        className="featuredCarousel__viewAllLink"
        onClick={() => navigate('/listings')}
      >
        {t('home.boostedListings.viewAll')} →
      </button>
    </div>
  );

  useEffect(() => {
    async function load() {
      const now = new Date().toISOString();
      // Only the columns the carousel + downstream PropertyCard + quality
      // scorer actually read. Drops body, features, sold_at, lat/lng,
      // contact_name, agency_name, epc_rating, user_id, show_map, etc.
      const CAROUSEL_FIELDS = 'id, title, price, price_label, type, beds, baths, sqm, city, area, images, description, address, plot_size, contact_phone, contact_email, status, is_boosted, boost_expires_at, is_featured, created_at';
      const [{ data: boostedData }, { data: organicPool }] = await Promise.all([
        supabase
          .from('listings').select(CAROUSEL_FIELDS).eq('is_boosted', true)
          .gt('boost_expires_at', now).order('boost_expires_at', { ascending: false }),
        supabase
          .from('listings').select(CAROUSEL_FIELDS).neq('is_boosted', true)
          .order('created_at', { ascending: false }).limit(60),
      ]);

      const mappedBoosted = (boostedData || []).filter(r => r.status !== 'archived' && r.status !== 'deleted').map(mapRow);
      const mappedOrganic = (organicPool || []).filter(r => r.status !== 'archived' && r.status !== 'deleted').map(mapRow);
      const qualityOrganic = mappedOrganic
        .filter(l => (l.images || []).length >= 5 && (l.description || '').length >= 200 && l.address && l.contact_phone && l.contact_email)
        .sort((a, b) => b.qualityScore - a.qualityScore);

      const byScore = [...mappedBoosted].sort((a, b) => computeFinalScore(b) - computeFinalScore(a));
      const sortedBoosted = rotateForSession(byScore);
      const organicTarget = sortedBoosted.length > 0 ? Math.max(1, Math.round(sortedBoosted.length * 3 / 7)) : 6;
      const organicSource = qualityOrganic.length > 0 ? qualityOrganic : mappedOrganic;
      const topOrganic = organicSource.slice(0, organicTarget);

      let combined = [];
      let bi = 0, oi = 0;
      while (bi < sortedBoosted.length || oi < topOrganic.length) {
        if (bi < sortedBoosted.length) combined.push(sortedBoosted[bi++]);
        if (oi < topOrganic.length)    combined.push(topOrganic[oi++]);
      }
      if (combined.length === 0) combined = mappedOrganic.slice(0, 6);

      setListings(combined);
      setLoading(false);
    }
    load();
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 600);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    function update() {
      if (window.innerWidth <= 900)  setCardWidth(280);
      else if (window.innerWidth <= 1100) setCardWidth(300);
      else setCardWidth(340);
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    function measure() {
      if (viewportRef.current) setVpWidth(viewportRef.current.offsetWidth);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const len = listings.length;

  // Desktop: circular navigation with animation lock
  const desktopPrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIndex(i => (i - 1 + len) % len);
    setTimeout(() => setIsAnimating(false), 700);
  };
  const desktopNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIndex(i => (i + 1) % len);
    setTimeout(() => setIsAnimating(false), 700);
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? desktopNext() : desktopPrev();
    touchStartX.current = null;
  };

  // Mobile: clamped navigation
  const maxMobile  = Math.max(0, len - 1);
  const mobilePrev = () => setIndex(i => Math.max(0, i - 1));
  const mobileNext = () => setIndex(i => Math.min(maxMobile, i + 1));

  const scrollToCard = (i) => {
    if (!mobileScrollRef.current) return;
    const el   = mobileScrollRef.current;
    const card = el.children[0];
    if (!card) return;
    el.scrollTo({ left: i * (card.offsetWidth + 8), behavior: 'smooth' });
  };

  useEffect(() => {
    if (isMobile) scrollToCard(index);
  }, [index, isMobile]);

  const onMobileScroll = () => {
    if (!mobileScrollRef.current) return;
    const el   = mobileScrollRef.current;
    const card = el.children[0];
    if (!card) return;
    const newIndex = Math.round(el.scrollLeft / (card.offsetWidth + 8));
    if (newIndex !== index) setIndex(newIndex);
  };

  if (loading) return (
    <section className="featured">
      <div className="featured__inner">
        {HEADER}
        <div style={{ display: 'flex', gap: 20, height: 380, alignItems: 'center', justifyContent: 'center' }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              flex: i === 1 ? '0 0 400px' : '0 0 300px',
              aspectRatio: '3/4',
              background: '#F3F4F6',
              borderRadius: 20,
              transform: i === 1 ? 'scale(1)' : 'scale(0.92)',
            }} />
          ))}
        </div>
      </div>
    </section>
  );

  if (listings.length === 0) return null;

  // Desktop: film-strip — render 3× listings so we can always scroll either direction without a seam
  const offsetStart      = len;
  const renderedListings = [...listings, ...listings, ...listings];
  // Place active card at exact viewport center: left edge at (vpWidth - cardWidth) / 2
  const translateX = -(offsetStart + index) * cardWidth + (vpWidth - cardWidth) / 2;

  if (isMobile) return (
    <section className="featured">
      <div className="featured__inner">
        {HEADER}
        <div className="featured__mobile-wrap">
          <button className={`featured__mobile-arrow ${index === 0 ? 'featured__mobile-arrow--off' : ''}`} onClick={mobilePrev} disabled={index === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div className="featured__mobile-scroll" ref={mobileScrollRef} onScroll={onMobileScroll}>
            {listings.map((listing, i) => (
              <div key={listing.id} className="featured__mobile-card">
                <PropertyCard listing={listing} index={i} />
              </div>
            ))}
          </div>
          <button className={`featured__mobile-arrow ${index === maxMobile ? 'featured__mobile-arrow--off' : ''}`} onClick={mobileNext} disabled={index === maxMobile}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
        <div className="featured__mobile-dots">
          {listings.map((_, i) => (
            <button key={i} className={`featured__mobile-dot ${i === index ? 'featured__mobile-dot--active' : ''}`} onClick={() => { setIndex(i); scrollToCard(i); }} />
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <section className="featured">
      <div className="featured__inner">
        {HEADER}

        <div className="featured__slider" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <button className="featured__arrow featured__arrow--left" onClick={desktopPrev} disabled={isAnimating}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>

          <div className="featured__viewport" ref={viewportRef}>
            <div
              className="featured__track"
              style={{
                transform: `translateX(${translateX}px)`,
                transition: `transform 700ms cubic-bezier(0.32, 0.72, 0, 1)`,
              }}
            >
              {renderedListings.map((listing, i) => {
                const isActive = i === offsetStart + index;
                const diff = i - (offsetStart + index);
                if (USE_WHITE_PROPERTY_CARDS) {
                  return (
                    <div
                      key={`${listing.id}-${i}`}
                      className={`featured__card featured__card--white${isActive ? ' featured__card--active' : ''}`}
                    >
                      <PropertyCard listing={listing} index={i} />
                    </div>
                  );
                }
                return (
                <div
                  key={`${listing.id}-${i}`}
                  className={`featured__card${isActive ? ' featured__card--active' : ''}`}
                  onClick={() => {
                    if (isAnimating) return;
                    if (diff === -1) desktopPrev();
                    else if (diff === 1) desktopNext();
                  }}
                >
                  {listing.image ? (
                    <img
                      src={listing.image}
                      alt={listing.title}
                      className="featured__card-img"
                      loading={isActive ? 'eager' : 'lazy'}
                      fetchpriority={isActive ? 'high' : 'auto'}
                      decoding="async"
                      width="640"
                      height="448"
                    />
                  ) : (
                    <div className="featured__card-gradient" style={{ background: listing.gradient }} />
                  )}
                  <div className="featured__card-overlay">
                    <div className="featured__card-content">
                      <p className="featured__card-location">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                        {listing.region || listing.city}
                      </p>
                      <h3 className="featured__card-title">{listing.title}</h3>
                      <p className="featured__card-price">{formatPrice(listing)}</p>
                      {isActive && (
                        <span
                          className="featured__card-cta"
                          onClick={e => { e.stopPropagation(); navigate(`/property/${listing.id}`); }}
                        >
                          View property →
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          <button className="featured__arrow featured__arrow--right" onClick={desktopNext} disabled={isAnimating}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        <div className="featured__dots">
          {listings.map((_, i) => (
            <button key={i} className={`featured__dot${i === index ? ' featured__dot--active' : ''}`} onClick={() => setIndex(i)} />
          ))}
        </div>
      </div>
    </section>
  );
}
